import { useState, useEffect, useCallback, useMemo } from 'react'
import { inject, observer } from 'mobx-react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LinkInvalid,
  Phone,
  ButtonStyled,
  Btn,
  ContentView,
} from './index.styled'
import { Box, Center, Divider } from '@chakra-ui/react'
import queryString from 'query-string'

import { Input, Invalid } from 'components/form'
import TurnstileView from 'components/Trunstile'
import { PrimaryBtn } from 'components/button'
import { useMessage } from 'hooks/ToastMessage'
import { config } from 'config'
import { isValidEmail } from 'utils/helper'

const ERROR_EXIST = 'exist'
const ERROR_INVALID = 'invalid'
const FORMAT_INVALID = 'format_invalid'

const EmailLogin = (props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const toastMessage = useMessage()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [isInvalid, setIsInvalid] = useState(false)
  const [error, setError] = useState('')

  const [turnstileToken, setTurnstileToken] = useState('')

  const { language } = props
  const {
    lang,
    clientId = '',
    redirectUri = '',
    state = '',
    codeChallenge = '',
  } = queryString.parse(location.search)

  const onInit = useCallback(() => {
    props.language.setLanguage(lang)
  }, [lang, props.language])

  useEffect(() => {
    onInit()
  }, [onInit])

  const onChange = (val) => {
    setEmail(val)
    setIsInvalid(false)
    setError('')
  }

  const onTurnstile = useCallback((token) => {
    setTurnstileToken(token)
  }, [])

  const onSubmit = async () => {
    if (email && !isValidEmail(email)) {
      setError(FORMAT_INVALID)
      setIsInvalid(true)
      return
    }
    try {
      setLoading(true)

      const validateResponse = await props.otp.validateEmail({
        email,
      })
      const isEmailExisting = validateResponse.data?.isEmailExisting
      const isEmailVerify = validateResponse.data?.isEmailVerify
      const isPinExist = validateResponse.data?.isPinExist

      if (isEmailExisting && isEmailVerify) {
        setLoading(false)

        const requestOtpResponse = await props.otp.requestEmailOtpRequest({
          email,
          token: turnstileToken,
        })
        if (requestOtpResponse) {
          navigate('/otp/verify', {
            state: {
              lang,
              clientId,
              redirectUri,
              state,
              codeChallenge,
              email,
              turnstileToken,
              prefix: '',
              refCode: requestOtpResponse.refcode,
              isPinExist,
              verifyType: 'email',
            },
          })
        }
      } else {
        setIsInvalid(true)
        setError(ERROR_EXIST)
        setLoading(false)
      }
    } catch (e) {
      setLoading(false)
      // toast({ title: e.toString() })
      if (e.message) {
        toastMessage({ title: e.message })
      } else {
        setError(ERROR_INVALID)
      }
    }
  }

  const getInvalid = useCallback(
    (errorCase) => {
      switch (errorCase) {
        case ERROR_EXIST: {
          return (
            <LinkInvalid>
              {language.getMsg(
                'This email was not found. You can ',
                'อีเมลยังไม่เคยสมัครบัญชี  คุณสามารถ'
              )}
              <a href={`${config.app.scheme}://createAccount`}>
                {language.getMsg('Register', 'สมัคร')}
              </a>
              {language.getMsg(
                'a new account with this email or try again.',
                'บัญชีใหม่ ด้วยอีเมลนี้ หรือเข้าสู่ระบบอีกครั้ง'
              )}
            </LinkInvalid>
          )
        }
        case ERROR_INVALID:
        case FORMAT_INVALID:
        default: {
          return (
            <Invalid
              invalid={language.getMsg(
                'This email is invalid. Please try again',
                'อีเมลนี้ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง'
              )}
            />
          )
        }
      }
    },
    [language]
  )

  const invalid = error ? getInvalid(error) : undefined
  const isDisabled = useMemo(
    () => turnstileToken === '' || isInvalid || email.trim() === '',
    [turnstileToken, isInvalid, email]
  )

  const style = { background: 'none' }
  return (
    <ContentView>
      <Box position="relative">
        <Phone>
          <Input
            type="text"
            label={language.getMsg(
              'Enter the email for OTP',
              'กรอกอีเมลเพื่อยืนยันบัญชี'
            )}
            value={email}
            onChange={onChange}
            invalid={isInvalid}
            containerStyled={style}
            inputStyled={style}
          />
        </Phone>
      </Box>
      {invalid}
      <Center marginTop={8}>
        <Divider orientation="horizontal" />
      </Center>
      <ButtonStyled>
        <TurnstileView onVerify={onTurnstile} />
        <Btn>
          <PrimaryBtn
            loading={loading}
            text={language.getMsg('Request OTP', 'ขอรหัส OTP')}
            disabled={isDisabled}
            onClick={onSubmit}
          />
        </Btn>
      </ButtonStyled>
    </ContentView>
  )
}

export default inject('otp', 'language')(observer(EmailLogin))
